import api from './client';

/**
 * The knowledge base — the curated corpus the platform reasons over.
 *
 * Distinct from `kb.js`, which is the payments-service scoring catalog. That
 * one holds the 22x17 score matrix; this one holds identity and attributes.
 * The two are joined by reference rather than merged, so the naming here is
 * deliberately explicit rather than shorter.
 */
class KnowledgeAPI {
  /** The entity-type registry, so a form can render itself. */
  static async types() {
    const response = await api.get('/knowledge/types');
    return response.data.data?.types ?? [];
  }

  /** Records of one type — what a picker needs. */
  static async list({ entityType, status = 'approved', q = '', limit = 200 } = {}) {
    const response = await api.get('/knowledge/records', { params: { entityType, status, q, limit } });
    return response.data.data ?? { items: [], total: 0 };
  }

  /**
   * The corpus counted by lifecycle domain — what the Data boxes and the
   * sidebar counts read.
   */
  static async dataDomains() {
    const response = await api.get('/knowledge/domains');
    return response.data?.data || {};
  }

  /**
   * Records, filtered by domain and paged.
   *
   * Separate from `list` because that one defaults to `status: 'approved'` and
   * a limit of 200 for the per-type screens, which would quietly hide a draft
   * and cap a domain holding 268 records at a page nobody could get past.
   */
  static async records({ domain = '', entityType = '', q = '', status = '', limit = 25, offset = 0 } = {}) {
    const response = await api.get('/knowledge/records', {
      params: { domain, entityType, q, status, limit, offset }
    });
    return response.data?.data || { items: [], total: 0, limit, offset };
  }

  /** Resolve a single kb:type:slug reference. */
  static async resolve(ref) {
    const response = await api.get('/knowledge/resolve', { params: { ref } });
    return response.data.data;
  }

  /** Create a record. Arrives as a draft unless the caller says otherwise. */
  static async create(record) {
    const response = await api.post('/knowledge/records', record);
    return response.data.data;
  }

  /**
   * Update a record. Identity is frozen — entityType and slug are in the URL,
   * never the body, because downstream records store the reference and a
   * rename would orphan them.
   */
  static async update(entityType, slug, patch) {
    const response = await api.put(`/knowledge/${entityType}/${slug}`, patch);
    return response.data.data;
  }

  static async approve(entityType, slug) {
    const response = await api.post(`/knowledge/${entityType}/${slug}/approve`, {});
    return response.data.data;
  }

  static async deprecate(entityType, slug, reason = '') {
    const response = await api.post(`/knowledge/${entityType}/${slug}/deprecate`, { reason });
    return response.data.data;
  }

  /**
   * Delete. Refused with 409 while anything still cites it — deleting a cited
   * record breaks the records that cite it, not the one being deleted, so the
   * refusal names them.
   */
  static async remove(entityType, slug, { force = false } = {}) {
    const response = await api.delete(`/knowledge/${entityType}/${slug}`, { params: force ? { force: true } : {} });
    return response.data.data;
  }

  // ── The schema overlay: what has been added to the code registry ──
  static async listSchemas(params = {}) {
    const response = await api.get('/knowledge/schemas', { params });
    return response.data?.data || {};
  }

  static async getSchema(code) {
    const response = await api.get(`/knowledge/schemas/${encodeURIComponent(code)}`);
    return response.data?.data || null;
  }

  /** Every recorded change to one schema, newest first. */
  static async schemaVersions(code, params = {}) {
    const response = await api.get(`/knowledge/schemas/${encodeURIComponent(code)}/versions`, { params });
    return response.data?.data || { versions: [] };
  }

  /**
   * Put a schema back to how a version left it.
   *
   * Adds a new version recording the restore rather than removing the ones in
   * between — the one moment anybody reads this history is when something has
   * gone wrong, and it must not lie about what happened.
   */
  /**
   * Put a schema under another, or back at the top level.
   *
   * An empty parent detaches — refusing to would make a mis-parented schema
   * permanent, with deleting it the only way out.
   */
  static async setSchemaParent(code, parent) {
    const res = await api.put(`/knowledge/schemas/${encodeURIComponent(code)}/parent`, { parent: parent || '' });
    return unwrap(res);
  }

  static async restoreSchemaVersion(code, version) {
    const res = await api.post(`/knowledge/schemas/${encodeURIComponent(code)}/versions/${encodeURIComponent(version)}/restore`, {});
    return unwrap(res);
  }

  static async createSchema(draft) {
    const response = await api.post('/knowledge/schemas', draft);
    return response.data?.data || null;
  }

  static async updateSchema(code, patch) {
    const response = await api.patch(`/knowledge/schemas/${encodeURIComponent(code)}`, patch);
    return response.data?.data || null;
  }

  static async deleteSchema(code) {
    const response = await api.delete(`/knowledge/schemas/${encodeURIComponent(code)}`);
    return response.data?.data || null;
  }

  static async addSchemaField(code, field) {
    const response = await api.post(`/knowledge/schemas/${encodeURIComponent(code)}/fields`, field);
    return response.data?.data || null;
  }

  static async updateSchemaField(code, name, patch) {
    const response = await api.patch(`/knowledge/schemas/${encodeURIComponent(code)}/fields/${encodeURIComponent(name)}`, patch);
    return response.data?.data || null;
  }

  static async deleteSchemaField(code, name) {
    const response = await api.delete(`/knowledge/schemas/${encodeURIComponent(code)}/fields/${encodeURIComponent(name)}`);
    return response.data?.data || null;
  }

  static async coverage() {
    const response = await api.get('/knowledge/coverage');
    return response.data.data;
  }

  static async integrity() {
    const response = await api.get('/knowledge/integrity');
    return response.data.data;
  }
}

export const knowledge = KnowledgeAPI;
export default KnowledgeAPI;
